import {
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { type FC, type ReactNode, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import { DESCRIPTIONS } from '@/constants/common/message.constants'

import {
  type ResolvedColumn,
  isLastVisibleFixedColumn,
  moveColumnTo,
  visibleFixedWidth
} from '../../-models/column-layout'
import {
  type HeaderCellRect,
  type HeaderDropTarget,
  resolveHeaderDropTarget
} from '../../-models/header-drop-target'

/** ポインタが表示範囲の端からこの距離に入ったら横スクロールする（px） */
const AUTO_SCROLL_EDGE = 48
/** 1 フレームあたりの横スクロール量（px） */
const AUTO_SCROLL_STEP = 16

type Props = {
  columns: ResolvedColumn[]
  labelOf: (key: string) => string
  /** 固定列の幅の合計の上限（px）。超える列は固定に入れない。未指定なら上限なし */
  maxFixedWidth?: number
  onMove: (next: ResolvedColumn[]) => void
  children: ReactNode
}

const readHeaderRects = (
  wrapper: HTMLElement,
  originX: number
): HeaderCellRect[] =>
  [
    ...wrapper.querySelectorAll<HTMLElement>(
      '.ant-table-thead th[data-column-key]'
    )
  ].map((th) => {
    const rect = th.getBoundingClientRect()
    return {
      key: th.dataset.columnKey ?? '',
      left: rect.left - originX,
      right: rect.right - originX,
      isFixed: th.classList.contains('ant-table-cell-fix-left')
    }
  })

/**
 * 一覧の見出しのドラッグで列を入れ替え、固定の範囲に出し入れする（#3690 VOC_PJ-577 案 1-B）。
 *
 * - クリック（ソート）とドラッグは 5px 動かしたかで分ける。ドラッグを離した直後のクリックは捨てる
 *   （同じ見出しの上で離すと antd のソートが反応するため）
 * - 動かすのは見出しの写しだけで、本体のセルは動かさない。落とし先は見出しと本体をまたぐ縦線で示す
 *   （行数 × 列数のセルを毎フレーム動かさないため）
 * - sticky ヘッダーは本体と別の表で overflow: hidden なので、dnd-kit の自動スクロールは効かない。
 *   本体（.ant-table-body）を自前で横スクロールさせる
 * - 落とし先の判定は resolveHeaderDropTarget（固定列の下に潜った列を選ばない）
 */
export const HeaderColumnDragArea: FC<Props> = ({
  columns,
  labelOf,
  maxFixedWidth,
  onMove,
  children
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const pointerXRef = useRef<number | null>(null)
  const suppressClickRef = useRef(false)
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [target, setTarget] = useState<HeaderDropTarget | null>(null)
  // ヒントを出す高さ（wrapper の上端からの距離）。sticky の見出しは縦スクロールしても画面上部に残るので、
  // 表の上端ではなく、表示中の見出しの下端に合わせる
  const [hintTop, setHintTop] = useState(0)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  )

  const updateTarget = () => {
    const wrapper = wrapperRef.current
    const pointerX = pointerXRef.current
    if (!wrapper || pointerX === null || !activeKey) return
    const wrapperRect = wrapper.getBoundingClientRect()
    const originX = wrapperRect.left
    const header = wrapper.querySelector<HTMLElement>('.ant-table-thead')
    if (header) {
      setHintTop(header.getBoundingClientRect().bottom - wrapperRect.top + 4)
    }
    setTarget(
      resolveHeaderDropTarget(
        readHeaderRects(wrapper, originX),
        pointerX - originX,
        activeKey,
        { firstLockedKey: columns[0]?.isLocked ? columns[0].key : undefined }
      )
    )
  }

  // ドラッグ中はポインタが止まっていても端にあればスクロールし続ける
  useEffect(() => {
    if (!activeKey) return
    let frame = 0
    const tick = () => {
      const wrapper = wrapperRef.current
      const body = wrapper?.querySelector<HTMLElement>('.ant-table-body')
      const pointerX = pointerXRef.current
      updateTarget()
      if (wrapper && body && pointerX !== null) {
        const bodyRect = body.getBoundingClientRect()
        const fixedRight = Math.max(
          bodyRect.left,
          ...readHeaderRects(wrapper, 0)
            .filter(({ isFixed }) => isFixed)
            .map(({ right }) => right)
        )
        if (pointerX > bodyRect.right - AUTO_SCROLL_EDGE) {
          body.scrollLeft += AUTO_SCROLL_STEP
          updateTarget()
        } else if (
          pointerX > fixedRight &&
          pointerX < fixedRight + AUTO_SCROLL_EDGE
        ) {
          body.scrollLeft -= AUTO_SCROLL_STEP
          updateTarget()
        }
      }
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
    // updateTarget は activeKey と ref だけを読むので、activeKey の変化で張り直せば足りる
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey])

  const handleDragStart = ({ active, activatorEvent }: DragStartEvent) => {
    setActiveKey(String(active.id))
    pointerXRef.current = (activatorEvent as PointerEvent).clientX
  }

  const handleDragMove = ({ activatorEvent, delta }: DragMoveEvent) => {
    pointerXRef.current = (activatorEvent as PointerEvent).clientX + delta.x
    updateTarget()
  }

  const finish = () => {
    setActiveKey(null)
    setTarget(null)
    pointerXRef.current = null
    suppressClickRef.current = true
    setTimeout(() => {
      suppressClickRef.current = false
    }, 0)
  }

  const handleDragEnd = ({ active }: DragEndEvent) => {
    const key = String(active.id)
    if (target) {
      const next = moveColumnTo(columns, key, target, { maxFixedWidth })
      if (next === columns && isTooWideToFix) {
        toast.error(DESCRIPTIONS.FIXED_COLUMNS_TOO_WIDE)
      } else if (next === columns && !target.isFixed) {
        toast.error(DESCRIPTIONS.CANNOT_UNFIX_LAST_COLUMN)
      } else if (next !== columns) {
        onMove(next)
      }
    }
    finish()
  }

  const activeColumn = columns.find(({ key }) => key === activeKey)
  const changesFixed =
    activeColumn !== undefined &&
    target !== null &&
    activeColumn.isFixed !== target.isFixed
  const isLastFixed =
    activeKey !== null &&
    target !== null &&
    !target.isFixed &&
    isLastVisibleFixedColumn(columns, activeKey)
  const isTooWideToFix =
    changesFixed &&
    target?.isFixed === true &&
    maxFixedWidth !== undefined &&
    visibleFixedWidth(columns) + activeColumn.width > maxFixedWidth
  const isBlocked = isLastFixed || isTooWideToFix
  const hint = isLastFixed
    ? DESCRIPTIONS.CANNOT_UNFIX_LAST_COLUMN
    : isTooWideToFix
      ? DESCRIPTIONS.FIXED_COLUMNS_TOO_WIDE
      : changesFixed
        ? target?.isFixed
          ? DESCRIPTIONS.DROP_TO_FIX
          : DESCRIPTIONS.DROP_TO_UNFIX
        : null

  return (
    <DndContext
      sensors={sensors}
      autoScroll={false}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={finish}
    >
      <div
        ref={wrapperRef}
        className="relative"
        onClickCapture={(event) => {
          if (!suppressClickRef.current) return
          event.stopPropagation()
          event.preventDefault()
        }}
      >
        {children}
        {activeKey && target && (
          <div
            className={
              isBlocked
                ? 'pointer-events-none absolute top-0 z-20 h-full w-0.5 bg-red'
                : 'pointer-events-none absolute top-0 z-20 h-full w-0.5 bg-primary'
            }
            style={{ left: target.lineX }}
            data-testid="header-drop-line"
          >
            {/* 見出しの写し（DragOverlay）と重ならないよう、見出しの段の下に出す */}
            {hint && (
              <span
                className={
                  isBlocked
                    ? 'absolute left-1 whitespace-nowrap rounded bg-red px-2 py-0.5 text-xs text-white'
                    : 'absolute left-1 whitespace-nowrap rounded bg-primary px-2 py-0.5 text-xs text-white'
                }
                style={{ top: hintTop }}
              >
                {hint}
              </span>
            )}
          </div>
        )}
      </div>
      <DragOverlay dropAnimation={null}>
        {activeKey && (
          <div className="cursor-grabbing whitespace-nowrap rounded border border-solid border-gray-200 bg-white px-3 py-1 text-sm font-bold shadow-md">
            {labelOf(activeKey)}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}
